import { useQueryClient } from '@tanstack/react-query';
import { Button, Spin, Typography } from 'antd';
import axios from 'axios';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import {
  clearSession,
  getRefreshToken,
  hasRefreshToken,
  isTransientError,
  refreshSession,
  setBusinessId,
  setSession,
  setSessionExpiredHandler,
} from '../api/client';
import { authApi } from '../api/endpoints';
import type { SafeUser } from '../api/types';

/** `unreachable`: we hold a refresh token but could not get a verdict from
 * the server (offline, timeout, api restarting). The session is kept and the
 * guard shows a retry screen instead of a spinner that never ends. */
type AuthStatus = 'loading' | 'authed' | 'anon' | 'unreachable';

/** Who the shell/router renders for. `platform-no-business`: the PLATFORM_ADMIN
 * before picking a business — only the businesses list exists for them. */
export type ShellMode = 'seller' | 'admin' | 'platform-no-business';

interface AuthContextValue {
  status: AuthStatus;
  user: SafeUser | null;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Local sign-out without hitting the API (used after a password change,
   * which revokes every refresh token server-side anyway). */
  dropSession: () => void;
  /** Re-read /auth/me — after the PLATFORM_ADMIN switches business, the
   * effective role/business come from the server, not from the token. */
  refreshMe: () => Promise<void>;
  /** Re-run the startup token exchange after an `unreachable` verdict. */
  retryBootstrap: () => void;
  /** A USER limited to a subset of stores: full admin inside those stores,
   * but whole-business figures (sklad debt, sklad pages, store-less rows)
   * are hidden — the backend enforces it, the shell just doesn't offer them. */
  isStoreRestricted: boolean;
  /** The cross-tenant PLATFORM_ADMIN account (in or out of a business). */
  isPlatformAdmin: boolean;
  /** Effective SUPERADMIN — a business's own, or the platform admin inside one. */
  isSuperadmin: boolean;
  /** False only for a PLATFORM_ADMIN who has not selected a business yet. */
  hasBusiness: boolean;
  shellMode: ShellMode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** /auth/me, tolerating a stale persisted business (archived/deleted since the
 * last visit): drop the selection and ask again with none. */
async function loadMe(): Promise<SafeUser> {
  try {
    return await authApi.me();
  } catch (error) {
    const code = axios.isAxiosError(error)
      ? (error.response?.data as { message?: string } | undefined)?.message
      : undefined;
    if (error && code === 'BUSINESS_NOT_FOUND') {
      setBusinessId(null);
      return authApi.me();
    }
    throw error;
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<SafeUser | null>(null);
  const queryClient = useQueryClient();

  const dropSession = useCallback((): void => {
    clearSession();
    setUser(null);
    setStatus('anon');
    queryClient.clear();
  }, [queryClient]);

  // Bootstrap: a stored refresh token is exchanged for a fresh pair (access
  // tokens never survive a reload — they live in memory only). Every network
  // call here has a timeout (api/client.ts), so this always settles: authed,
  // anon, or unreachable (session kept, retry offered) — never a spinner
  // that outlives the user's patience.
  const [bootAttempt, setBootAttempt] = useState(0);
  const retryBootstrap = useCallback((): void => setBootAttempt((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!hasRefreshToken()) {
        setStatus('anon');
        return;
      }
      setStatus('loading');
      const result = await refreshSession();
      if (cancelled) return;
      if (result !== 'ok') {
        setStatus(result === 'unreachable' ? 'unreachable' : 'anon');
        return;
      }
      try {
        const me = await loadMe();
        if (cancelled) return;
        setUser(me);
        setStatus('authed');
      } catch (error) {
        if (cancelled) return;
        if (isTransientError(error)) {
          setStatus('unreachable');
        } else {
          clearSession();
          setStatus('anon');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootAttempt]);

  // The axios layer reports a dead session (failed mid-flight refresh) here,
  // so the route guard redirects instead of pages erroring forever.
  useEffect(() => {
    setSessionExpiredHandler(dropSession);
    return () => setSessionExpiredHandler(() => {});
  }, [dropSession]);

  const login = useCallback(async (loginName: string, password: string): Promise<void> => {
    const pair = await authApi.login(loginName, password);
    setSession(pair);
    // A tenant user must never carry a platform admin's leftover selection; the
    // platform admin resumes the business they had picked on this device.
    if (!pair.user.platformAdmin) setBusinessId(null);
    setUser(pair.user.platformAdmin ? await loadMe() : pair.user);
    setStatus('authed');
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const refreshToken = getRefreshToken();
    dropSession();
    if (refreshToken) await authApi.logout(refreshToken).catch(() => {});
  }, [dropSession]);

  const refreshMe = useCallback(async (): Promise<void> => {
    setUser(await loadMe());
  }, []);

  const isStoreRestricted = user?.role === 'USER' && (user.storeIds?.length ?? 0) > 0;
  const isPlatformAdmin = user?.platformAdmin === true;
  const isSuperadmin = user?.role === 'SUPERADMIN';
  const hasBusiness = user?.businessId != null;
  const shellMode: ShellMode =
    user?.role === 'SELLER'
      ? 'seller'
      : isPlatformAdmin && !hasBusiness
        ? 'platform-no-business'
        : 'admin';
  const value = useMemo(
    () => ({
      status,
      user,
      login,
      logout,
      dropSession,
      refreshMe,
      retryBootstrap,
      isStoreRestricted,
      isPlatformAdmin,
      isSuperadmin,
      hasBusiness,
      shellMode,
    }),
    [
      status,
      user,
      login,
      logout,
      dropSession,
      refreshMe,
      retryBootstrap,
      isStoreRestricted,
      isPlatformAdmin,
      isSuperadmin,
      hasBusiness,
      shellMode,
    ],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** Startup splash. After a few seconds the spinner gains a line of text so a
 * slow mobile link reads as «still connecting», not as a frozen app. */
function BootSplash(): JSX.Element {
  const { t } = useTranslation();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), 5_000);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '40vh' }}
    >
      <Spin size="large" />
      <Typography.Text type="secondary" style={{ marginTop: 16, minHeight: 22 }}>
        {slow ? t('boot.slow') : t('boot.connecting')}
      </Typography.Text>
    </div>
  );
}

/** Startup could not reach the server: the session is intact, offer a retry
 * (and a full reload as the escape hatch for a wedged service worker). */
function BootUnreachable({
  onRetry,
  onSignOut,
}: {
  onRetry: () => void;
  onSignOut: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '35vh 24px 0',
        textAlign: 'center',
      }}
    >
      <Typography.Title level={4}>{t('boot.unreachable')}</Typography.Title>
      <Typography.Paragraph type="secondary">{t('boot.unreachableHint')}</Typography.Paragraph>
      <Button type="primary" size="large" onClick={onRetry} style={{ minWidth: 200 }}>
        {t('common.retry')}
      </Button>
      <Button type="link" onClick={() => window.location.reload()} style={{ marginTop: 8 }}>
        {t('boot.reload')}
      </Button>
      <Button type="link" danger onClick={onSignOut}>
        {t('boot.signOut')}
      </Button>
    </div>
  );
}

/** Route guard: unauthenticated visitors land on /login. */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { status, retryBootstrap, dropSession } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <BootSplash />;
  if (status === 'unreachable') {
    return <BootUnreachable onRetry={retryBootstrap} onSignOut={dropSession} />;
  }
  if (status === 'anon') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
